import { useCallback, useState } from 'react'
import { useGlobalStore } from '@highlight/ipc/react'
import { useAtom } from 'jotai'
import { contextNotificationMockAtom, type ContextNotificationMock } from '../../context-notification/state'
import { useQueryClient } from '@tanstack/react-query'
import { STAGED_ACTIONS_QUERY_KEY, type StagedAction } from '../../../hooks/use-staged-actions'
import { pulseChatInput } from '../../../hooks/use-chat-input-pulse'
import {
  ASK_USER_QUESTION_STAGED_TOOLSET,
  ASK_USER_QUESTION_STAGED_TOOLNAME,
} from '@renderer/chat/integrations/ask-user-question'
import { useIsLoggedIn } from '@renderer/hooks/useIsLoggedIn'
import { AUDIO_NOTES_QUERY_KEY } from '@renderer/hooks/audio-notes'
import type { AudioNote } from '@highlight/common'
import { useAudioNoteHasMeeting, useHasLiveAudioNote } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useStartAudioNote } from '@renderer/hooks/useStartAudioNote'
import { useSetSidepanelState } from '@renderer/overlay/atoms'
import { useSidepanelLocked } from '@renderer/overlay/Sidepanel/atoms'
import { useViewActions, useViewNavigation } from '@renderer/overlay/Sidepanel/view-atoms'
import { useSidepanelSurface } from '@renderer/overlay/Sidepanel/profile/use-sidepanel-profile'
import { viewContextOf } from '../../../view-definitions'
import { useLiveMeetingIdentity } from '../../meeting/use-live-meeting-identity'
import { MEETING_VIEW_ID } from '../../meeting/meeting-view-id'
import { useNewMeetingContext } from '../../meeting/use-new-meeting-context'
import {
  Book,
  BookOpenText,
  Brain,
  Check,
  Download,
  Gift,
  History,
  Keyboard,
  EllipsisVertical,
  Mic,
  MessageCircleQuestion,
  Plus,
  Settings,
  Sparkles,
  SquarePen,
} from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button, LoadingState, Menu, Tooltip } from '@highlight/ui'
import WindowIpcRenderer from '@renderer/ipc/WindowIpc.renderer'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useIncognitoMode } from '@renderer/hooks/useIncognitoMode'
import { SidepanelIncognitoButton } from '../sidepanel-incognito-button'
import { ACTIVE_TOOLBAR_BUTTON_CLASS } from '../toolbar-button-class'
import { SidepanelHelpMenu } from './help-menu'
import { noteNoun } from '@shared/utils/audio-notes/AudioNoteUtils'
import { isDev } from '@shared/utils/platform'
import { isAlpha } from '@shared/utils/builds/channels.shared'
import { useOpenSettingsAndEmit } from '@renderer/overlay/Sidepanel/utils/use-open-settings-and-emit'
import { useOpenFeedbackInSettings } from '@renderer/overlay/Sidepanel/utils/use-open-feedback-in-settings'
import { BetaFeedback } from '@renderer/components/beta-feedback'
import { AudioWaveform } from '@renderer/overlay/components/audio-waveform'
import { REMINDERS_QUERY_KEY, type ReminderApiItem } from '../../../hooks/use-reminders'
import { isBrowserRuntime, navigateToHighlightRoute } from '@renderer/browser/browser-utils'
import { useUpdateState } from '@renderer/hooks/use-update-state'

const CONTEXT_NOTIFICATION_MOCKS: ReadonlyArray<{ mode: ContextNotificationMock; label: string }> = [
  { mode: 'progress', label: 'In progress' },
  { mode: 'minimized', label: 'Minimized' },
  { mode: 'finished', label: 'Finished' },
]

export function SidepanelToolbarSection() {
  const setSidepanelState = useSetSidepanelState()
  // `hasLiveNote` includes `saving`, so a save in flight can't be raced by
  // starting a second recording from the menu.
  const hasActiveNote = useHasLiveAudioNote()
  const hasMeeting = useAudioNoteHasMeeting()
  const { startAudioNote } = useStartAudioNote()
  const queryClient = useQueryClient()
  const { openView } = useViewActions()
  const liveMeeting = useLiveMeetingIdentity()
  const newMeeting = useNewMeetingContext()
  const { topView } = useViewNavigation()
  const topChatContext = viewContextOf(topView, 'chat')
  const [automaticUpdates] = useGlobalStore<boolean>('settings', 'updates.automatic', true)
  const { update, labels, onClickCTA } = useUpdateState()

  const { isIncognitoEnabled } = useIncognitoMode()
  const isHomeNavigation = useSidepanelSurface('navigation') === 'home'
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [devModeEnabled] = useGlobalStore<boolean>('settings', 'dev-mode', false)
  const [contextNotificationMock, setContextNotificationMock] = useAtom(contextNotificationMockAtom)
  // One mock at a time: checking a state replaces the current one, unchecking clears it.
  const toggleContextNotificationMock = useCallback(
    (mode: ContextNotificationMock) => (checked: boolean) => setContextNotificationMock(checked ? mode : null),
    [setContextNotificationMock],
  )

  const isHistoryActive = topView.id === 'history'
  const isNewChatActive = !!topChatContext?.isNew

  // Learn menu hooks
  const isLoggedIn = useIsLoggedIn()
  const openSettingsAndEmit = useOpenSettingsAndEmit()
  const openBetaFeedback = useOpenFeedbackInSettings('sidepanel header')
  const sidepanelLocked = useSidepanelLocked()

  const openSettingsWindow = useCallback(() => {
    if (isBrowserRuntime()) {
      navigateToHighlightRoute('/settings')
      return
    }
    if (!sidepanelLocked) {
      setSidepanelState('hidden')
    }
    WindowIpcRenderer.openSettingsWindow()
  }, [setSidepanelState, sidepanelLocked])

  const handleNewChat = useCallback(() => {
    AmplitudeRenderer.trackEvent('Chat New Chat', { source: 'sidepanel toolbar', alreadyNew: isNewChatActive })

    // Already in the new chat — acknowledge by pulsing the input
    if (isNewChatActive) {
      pulseChatInput()
      return
    }

    // Don't clear the input here — draft persistence would then stash the empty
    // string over the outgoing entry's unsent text. The fresh entry key has no
    // draft, so the persistence hook restores an empty input on its own.
    openView('chat', { isNew: true }, { sourceLabel: 'Home' })
  }, [isNewChatActive, openView])

  // Learn submenu handlers
  const handleOpenDocumentation = useCallback(() => {
    window.open('https://highlightai.com/support', '_blank', 'noopener,noreferrer')
    AmplitudeRenderer.trackEvent('Get Help', { action: 'Documentation' })
  }, [])

  const handleOpenHotkeys = useCallback(() => {
    openSettingsAndEmit('open-hotkeys-modal')
    AmplitudeRenderer.trackEvent('Get Help', { action: 'Hotkeys Overview' })
  }, [openSettingsAndEmit])

  const handleWhatsNew = useCallback(() => {
    openSettingsAndEmit('open-changelog-modal', { useLatest: false })
    AmplitudeRenderer.trackEvent('Get Help', { action: "What's New" })
  }, [openSettingsAndEmit])

  const handleOpenHistory = useCallback(() => {
    if (isHistoryActive) return
    openView('history', undefined, { sourceLabel: 'Home' })
  }, [isHistoryActive, openView])

  const handleMeetingNote = useCallback(() => {
    if (hasActiveNote) {
      openView(MEETING_VIEW_ID, liveMeeting, { sourceLabel: 'Home' })
      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'open active meeting',
        source: 'sidepanel menu',
      })
      return
    }
    AmplitudeRenderer.trackEvent('Audio Note Action', {
      action: 'start taking notes',
      source: 'sidepanel menu',
    })
    startAudioNote({
      source: 'assistant',
      onSuccessfulStart: () => {
        openView(MEETING_VIEW_ID, newMeeting, { sourceLabel: 'Home' })
      },
    })
  }, [hasActiveNote, liveMeeting, newMeeting, openView, startAudioNote])

  // A fully-populated agent-authored reminder: source attribution, a due pill,
  // and the unread `new` treatment. Injected into the cache rather than POSTed
  // because `source` is server-set (the REST create path always passes null), so
  // an API-created reminder can only ever be a bare manual one. `priority` has to
  // follow the home section's filter — it renders only the active list, so a
  // reminder landing in the other one looks like nothing happened.
  const peekCachedAudioNote = useCallback((): AudioNote | undefined => {
    // Dev mocks only — read whatever history/memory already hydrated; never
    // subscribe the Home toolbar to the full audio-notes corpus.
    const cached = queryClient.getQueriesData<{ pages?: Array<{ data?: Array<AudioNote> }> }>({
      queryKey: [AUDIO_NOTES_QUERY_KEY],
    })
    for (const [, data] of cached) {
      const first = data?.pages?.[0]?.data?.[0]
      if (first) {
        return first
      }
    }
    return undefined
  }, [queryClient])

  const handleMockReminder = useCallback(() => {
    const now = new Date().toISOString()
    const sourceNote = peekCachedAudioNote()
    const mockItem: ReminderApiItem = {
      id: `mock-reminder-${Date.now()}`,
      text: 'Send Sam the updated API migration timeline before Thursday’s review',
      dueAt: new Date(Date.now() + 45 * 60 * 1000).toISOString(),
      type: 'task',
      source: {
        type: 'audioNote',
        id: sourceNote?.id ?? '00000000-0000-0000-0000-000000000000',
        title: sourceNote?.title ?? 'Weekly standup sync',
      },
      createdAt: now,
      updatedAt: now,
      resolvedAt: null,
      attachments: [],
      // Marks it agent-authored rather than hand-typed (see getReminderCreationMethod).
      jobId: '00000000-0000-0000-0000-000000000001',
      // No `readAt`, so the cell renders in its unread `new` state.
      metadata: null,
      priority: false,
    }
    queryClient.setQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY], (old) => [mockItem, ...(old ?? [])])
    console.log('[Mock] Reminder injected into the open list')
  }, [peekCachedAudioNote, queryClient])

  // Mirrors what the Linear hydrator produces for a `save_issue` staged action:
  // toolSet/toolName carry the routing (`actionType` was dropped server-side),
  // inputData is the MCP tool input, and the view is the hydrator's cached render.
  const handleMockLinearAction = useCallback(() => {
    const now = new Date().toISOString()
    const sourceNote = peekCachedAudioNote()
    const mockAction: StagedAction = {
      id: `mock-linear-${Date.now()}`,
      createdAt: now,
      updatedAt: now,
      userId: 'mock-user',
      lastInvokedAt: null,
      archivedAt: null,
      resolvedAt: null,
      actionType: undefined,
      toolSet: 'linear',
      toolName: 'save_issue',
      connectionProvider: 'linear',
      metadata: { source: 'mock_action' },
      outputData: null,
      title: 'Create Issue: Fix authentication bug in login flow',
      description:
        'Investigate and fix the authentication bug that occurs during login. The ticket includes reproduction steps and is assigned to the Engineering team.',
      inputData: {
        title: 'Fix authentication bug in login flow',
        description:
          'Investigate and fix the authentication bug that occurs during login.\n\n## Steps to reproduce:\n1. Open the app\n2. Click login button\n3. Enter valid credentials\n4. Observe error message',
        team: 'mock-team-engineering',
        teamName: 'Engineering',
        assignee: 'mock-user-dev',
        assigneeName: 'Mock Developer',
        priority: 1,
        labels: ['mock-label-bug'],
        labelNames: ['Bug'],
      },
      references: [
        {
          title: sourceNote?.title ?? 'Weekly standup sync',
          type: 'audioNote',
          // The user's latest real note, so the source row deep-links somewhere
          // and the action shows up in that meeting's "For You" list.
          ...(sourceNote?.id && { audioNoteId: sourceNote.id }),
          reasoning: 'Source meeting note from the standup discussion about the authentication bug.',
        },
      ],
      sourceIntegration: 'linear',
      sourceMetadata: { originSkill: 'Standup Action Items' },
      view: {
        title: 'Create Issue: Fix authentication bug in login flow',
        content:
          'Investigate and fix the authentication bug that occurs during login.\n\n**Steps to reproduce:**\n1. Open the app\n2. Click login button\n3. Enter valid credentials\n4. Observe error message',
        icon: '',
        tags: [
          { key: 'Team', value: 'Engineering' },
          { key: 'Status', value: 'Todo' },
          { key: 'Assignee', value: 'Mock Developer' },
          { key: 'Label', value: 'Bug' },
          { key: 'Priority', value: 'Urgent' },
        ],
        tagOrder: ['Team', 'Status', 'Assignee', 'Label', 'Priority', 'Project'],
        buttons: [],
        invokeLabel: 'Create Ticket',
        invokeIcon: 'https://cdn.highlightai.com/media/icons/ticket.svg',
        supportsUndo: true,
      },
    }
    queryClient.setQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY], (old) => [mockAction, ...(old ?? [])])
    console.log('[Mock] Linear action injected into cache')
  }, [peekCachedAudioNote, queryClient])

  // Mirrors what the backend's surfaceBackgroundQuestion creates: the synthetic
  // ask_user_question/question staged action with the questions in inputData and
  // the options-only markdown body (the title carries the question text once).
  // Exercises the "Highlight has a question" toast, the panel row (Answer opens
  // the drawer), and the drawer's interactive card + paused-run framing.
  // Note: submitting the answer POSTs to /staged-actions/:id/answer for an
  // action that doesn't exist server-side, so Submit demos the error/retry path.
  const handleMockQuestionAction = useCallback(() => {
    const question = 'Which environment should I deploy the staging fix to?'
    const now = new Date().toISOString()
    const mockAction: StagedAction = {
      id: `mock-question-${Date.now()}`,
      createdAt: now,
      updatedAt: now,
      userId: 'mock-user',
      lastInvokedAt: null,
      archivedAt: null,
      resolvedAt: null,
      actionType: undefined,
      toolSet: ASK_USER_QUESTION_STAGED_TOOLSET,
      toolName: ASK_USER_QUESTION_STAGED_TOOLNAME,
      connectionProvider: '',
      metadata: { source: 'ask_user_question' },
      outputData: null,
      title: question,
      description: '- Staging\n- Production\n- Hold off for now',
      inputData: {
        questions: [
          {
            question,
            header: 'Env',
            multiSelect: false,
            options: [
              { label: 'Staging', description: 'Safe, non-prod environment' },
              { label: 'Production', description: 'Live users — irreversible' },
              { label: 'Hold off for now', description: 'Pause the rollout until we talk' },
            ],
          },
        ],
      },
      references: [
        {
          title: 'Monday core sync',
          type: 'audioNote',
          reasoning: 'The plan item this question is paused on came from the Monday core sync.',
        },
      ],
      view: {
        title: question,
        content: '- Staging\n- Production\n- Hold off for now',
        icon: '',
        tags: [],
        buttons: [],
        invokeLabel: 'Answer',
        supportsUndo: false,
      },
    }
    queryClient.setQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY], (old) => [mockAction, ...(old ?? [])])
    console.log('[Mock] Question action injected into cache')
  }, [queryClient])

  return (
    <Tooltip.Provider>
      <div className="flex items-center gap-1">
        <BetaFeedback
          source="sidepanel header"
          side="bottom"
          align="end"
          openFeedback={openBetaFeedback}
          className="mr-1"
        />

        {automaticUpdates && labels && (update?.state === 'ready' || update?.state === 'installing') && (
          <Tooltip>
            {/* A wrapper, not the button: it is disabled while installing, and a disabled button gets no pointer events. */}
            <Tooltip.Trigger render={<div className="relative flex" />}>
              <Button
                variant="ghost"
                shape="square"
                onClick={onClickCTA}
                disabled={update?.state === 'installing'}
                aria-label={labels.title}
              >
                {update?.state === 'ready' ? (
                  <Download className="text-success-subtle" />
                ) : (
                  <LoadingState.Spinner className="text-success-subtle size-4" />
                )}
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content side="bottom">{labels.title}</Tooltip.Content>
          </Tooltip>
        )}
        {isLoggedIn && (
          <>
            <Tooltip>
              <Tooltip.Trigger
                render={
                  <Button
                    variant="ghost"
                    shape="square"
                    onClick={handleOpenHistory}
                    aria-label="History"
                    className={twMerge(isHistoryActive && ACTIVE_TOOLBAR_BUTTON_CLASS)}
                  >
                    <History />
                  </Button>
                }
              />
              <Tooltip.Content side="bottom">History</Tooltip.Content>
            </Tooltip>

            {!isHomeNavigation && <SidepanelIncognitoButton />}
          </>
        )}

        <Menu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <Menu.Trigger
            variant="ghost"
            shape="square"
            aria-label="Menu"
            data-tour-section="toolbar-menu"
            className={twMerge(isMenuOpen && ACTIVE_TOOLBAR_BUTTON_CLASS)}
          >
            <EllipsisVertical />
          </Menu.Trigger>

          <Menu.Content>
            {isLoggedIn && (
              <>
                <Menu.Item onClick={handleNewChat}>
                  <SquarePen />
                  <span>New chat</span>
                </Menu.Item>

                <Menu.Item onClick={handleMeetingNote} disabled={isIncognitoEnabled && !hasActiveNote}>
                  {hasActiveNote ? (
                    // A div, not an svg, so it needs the lead slot to sit in the icon lane
                    <Menu.ItemLead>
                      <AudioWaveform />
                    </Menu.ItemLead>
                  ) : (
                    <Mic />
                  )}
                  <span>{hasActiveNote ? `Open ${noteNoun(hasMeeting)}` : 'New audio note'}</span>
                </Menu.Item>

                {isHomeNavigation && <SidepanelIncognitoButton submenu />}

                <Menu.Separator />
              </>
            )}

            {/* Settings */}
            <Menu.Item onClick={openSettingsWindow} data-tour-section="toolbar-settings">
              <Settings />
              <span>Settings</span>
            </Menu.Item>

            <SidepanelHelpMenu />

            {/* Learn submenu */}
            <Menu.SubmenuRoot>
              <Menu.SubmenuTrigger>
                <BookOpenText />
                <span>Learn</span>
              </Menu.SubmenuTrigger>
              <Menu.Content>
                <Menu.Item onClick={handleOpenDocumentation}>
                  <Book />
                  <span>Documentation</span>
                </Menu.Item>
                <Menu.Item onClick={handleOpenHotkeys}>
                  <Keyboard />
                  <span>Hotkeys</span>
                </Menu.Item>
                {isLoggedIn && (
                  <Menu.Item onClick={handleWhatsNew}>
                    <Gift />
                    <span>What's new</span>
                  </Menu.Item>
                )}
              </Menu.Content>
            </Menu.SubmenuRoot>

            {/* Developer section - only visible in dev mode or alpha/dynamic channels */}
            {devModeEnabled && (isDev || isAlpha()) && (
              <>
                <Menu.Separator />
                <Menu.Group>
                  <Menu.GroupLabel>Developer</Menu.GroupLabel>

                  <Menu.Item onClick={handleMockLinearAction}>
                    <Plus />
                    <span>Mock Linear action</span>
                  </Menu.Item>

                  <Menu.Item onClick={handleMockQuestionAction}>
                    <MessageCircleQuestion />
                    <span>Mock question action</span>
                  </Menu.Item>

                  <Menu.Item onClick={handleMockReminder}>
                    <Brain />
                    <span>Mock reminder</span>
                  </Menu.Item>

                  <Menu.SubmenuRoot>
                    <Menu.SubmenuTrigger>
                      <Sparkles />
                      <span>Mock context notification</span>
                    </Menu.SubmenuTrigger>
                    <Menu.Content>
                      {CONTEXT_NOTIFICATION_MOCKS.map(({ mode, label }) => (
                        <Menu.CheckboxItem
                          key={mode}
                          checked={contextNotificationMock === mode}
                          onCheckedChange={toggleContextNotificationMock(mode)}
                          closeOnClick={false}
                        >
                          <Menu.CheckboxItemIndicator>
                            <Check className="size-3.5" />
                          </Menu.CheckboxItemIndicator>
                          <span>{label}</span>
                        </Menu.CheckboxItem>
                      ))}
                    </Menu.Content>
                  </Menu.SubmenuRoot>
                </Menu.Group>
              </>
            )}
          </Menu.Content>
        </Menu>
      </div>
    </Tooltip.Provider>
  )
}
